// Shared drag + QWERTY→Ottoman mapping for search bar and grid keyboards.
// localStorage key for persisting Ottoman keyboard drag position across sessions
export const LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY = "lexiqamusSearchOttomanKeyboardPosition"

// CSS class applied during active drag operation
export const LEXIQAMUS_OTTOMAN_KEYBOARD_DRAGGED_CLASS = "lexiqamus-search-keyboard--dragging"

// Clamps a numeric value between min and max bounds (used for drag positioning)
function clampLexiqamusDrag(n, min, max) {
  return Math.min(max, Math.max(min, n))
}

// Attaches drag functionality to a floating keyboard panel via a drag handle.
// Persists position to localStorage using the given storageKey (defaults to
// LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY for backward compatibility).
// Returns cleanup function to remove event listeners.
export function attachLexiqamusOttomanKeyboardDrag(panelEl, dragHandleEl, storageKey = LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY) {
  if (!panelEl || !dragHandleEl) return () => {}

  let armed = false
  let dragging = false
  let offsetX = 0
  let offsetY = 0

  // Converts panel position from relative/transformed to absolute viewport coordinates
  const freezePositionFromViewport = () => {
    const rect = panelEl.getBoundingClientRect()
    panelEl.style.left = `${rect.left}px`
    panelEl.style.top = `${rect.top}px`
    panelEl.style.right = "auto"
    panelEl.style.bottom = "auto"
    panelEl.style.transform = "none"
  }

  // Saves current panel position to localStorage for persistence across sessions
  const persist = () => {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ top: panelEl.style.top, left: panelEl.style.left })
      )
    } catch (_e) {
      // ignore — localStorage may be unavailable (e.g. private browsing)
    }
  }

  // Updates panel position during drag, clamped to viewport bounds with margin
  const onPointerMove = (e) => {
    if (!armed || !dragging) return
    e.preventDefault()
    const panelWidth  = panelEl.offsetWidth
    const panelHeight = panelEl.offsetHeight
    const margin = 4
    const left = clampLexiqamusDrag(e.clientX - offsetX, margin, window.innerWidth - panelWidth - margin)
    const top  = clampLexiqamusDrag(e.clientY - offsetY, margin, window.innerHeight - panelHeight - margin)
    panelEl.style.left  = `${left}px`
    panelEl.style.top   = `${top}px`
    panelEl.style.right = "auto"
  }

  // Cleans up drag state and persists final position
  const endDrag = () => {
    if (!armed) return
    armed = false
    document.removeEventListener("pointermove", onPointerMove)
    document.removeEventListener("pointerup", onPointerUp)
    document.removeEventListener("pointercancel", onPointerUp)
    if (dragging) {
      panelEl.classList.remove(LEXIQAMUS_OTTOMAN_KEYBOARD_DRAGGED_CLASS)
      persist()
    }
    dragging = false
  }

  const onPointerUp = () => {
    endDrag()
  }

  // Initiates drag on left-click of drag handle (excludes action buttons)
  const onPointerDown = (e) => {
    if (e.button !== 0) return
    if (e.target.closest(".ottoman-keyboard-action-btn")) return
    if (e.target.closest("button")) return
    if (!dragHandleEl.contains(e.target)) return

    e.preventDefault()
    armed   = true
    dragging = true
    freezePositionFromViewport()
    offsetX = e.clientX - panelEl.getBoundingClientRect().left
    offsetY = e.clientY - panelEl.getBoundingClientRect().top
    panelEl.classList.add(LEXIQAMUS_OTTOMAN_KEYBOARD_DRAGGED_CLASS)
    document.addEventListener("pointermove", onPointerMove, { passive: false })
    document.addEventListener("pointerup", onPointerUp, true)
    document.addEventListener("pointercancel", onPointerUp, true)
  }

  dragHandleEl.addEventListener("pointerdown", onPointerDown)

  return () => {
    dragHandleEl.removeEventListener("pointerdown", onPointerDown)
    endDrag()
  }
}

// Maps Turkish QWERTY key characters to Ottoman characters for Turkish locale.
// Uses event.key (actual typed character) rather than event.code (physical position)
// because Turkish keys (ı, ğ, ü, ş, ö, ç) differ from standard QWERTY positions.
// Supports Shift modifiers and ALT combinations.
export function ottomanCharForTurkishKey(key, shift, alt) {
  // Turkish case: "I" lowers to "ı", "İ" lowers to "i". Plain toLowerCase folds both into "i".
  const k = key.toLocaleLowerCase("tr")

  if (alt && !shift) {
    const altMap = { z: "ذ", s: "ث", t: "ۃ" }
    return altMap[k] || null
  }

  if (alt) return null

  // A listed Shift letter replaces the base one. Every other key keeps its base letter,
  // so ı/I, ç/Ç, ğ/Ğ and ş/Ş do not depend on Shift being up.
  if (shift) {
    const shiftMap = { t: "ط", a: "ء", s: "ص", d: "ض", h: "ح", z: "ظ", n: "ڭ", w: "ؤ", e: "ۀ", y: "ئ" }
    if (shiftMap[k]) return shiftMap[k]
  }

  const base = {
    q: "ق", w: "و", e: "ە", r: "ر", t: "ت",
    y: "ی", u: "و", ı: "ع", o: "و", p: "پ",
    ğ: "غ", ü: "و",
    a: "ا", s: "س", d: "د", f: "ف", g: "گ",
    h: "ه", j: "ژ", k: "ك", l: "ل", ş: "ش",
    i: "ع",
    z: "ز", x: "خ", c: "ج", v: "و", b: "ب",
    n: "ن", m: "م", ö: "و", ç: "چ"
  }
  return base[k] || null
}

// Maps physical keyboard keys to Ottoman characters for LexiQamus Ottoman mode.
// Supports base keys, Shift modifiers, and Alt combinations (ALT+n).
// Returns Ottoman character or null if no mapping exists.
//
// Note: multiple Latin keys intentionally map to the same Ottoman character
// (e.g. U/O/V → و, Y/I → ی) — this mirrors the Ottoman phonetic layout.
export function ottomanCharForPhysicalKey(code, shift, alt) {
  // Multi-character keys with Shift and Alt variants
  switch (code) {
    case "KeyW":
      if (alt) return null
      return shift ? "ؤ" : "و"
    case "KeyE":
      if (alt) return null
      return shift ? "ۀ" : "ع"
    case "KeyY":
      if (alt) return null
      return shift ? "ئ" : "ی"
    case "KeyT":
      if (alt && !shift) return "ث"
      if (shift && !alt) return "ط"
      if (!shift && !alt) return "ت"
      return null
    case "KeyA":
      if (alt) return null
      return shift ? "ء" : "ا"
    case "KeyS":
      if (alt && !shift) return "ص"
      if (shift && !alt) return "ش"
      if (!shift && !alt) return "س"
      return null
    case "KeyD":
      if (alt) return null
      return shift ? "ض" : "د"
    case "KeyG":
      if (alt) return null
      return shift ? "غ" : "ج"
    case "KeyH":
      if (alt && !shift) return "ۃ"
      if (shift && !alt) return "ح"
      if (!shift && !alt) return "ه"
      return null
    case "KeyK":
      if (alt) return null
      return shift ? "گ" : "ك"
    case "KeyZ":
      if (alt && !shift) return "ذ"
      if (shift && !alt) return "ظ"
      if (!shift && !alt) return "ز"
      return null
    case "KeyN":
      if (alt) return null
      return shift ? "ڭ" : "ن"
    default:
      break
  }

  // Single-character keys (no Shift/Alt variants)
  if (alt || shift) return null

  const singles = {
    KeyQ: "ق",
    KeyR: "ر",
    KeyU: "و",
    KeyI: "ی",
    KeyO: "و",
    KeyP: "پ",
    KeyF: "ف",
    KeyJ: "ژ",
    KeyL: "ل",
    KeyX: "خ",
    KeyC: "چ",
    KeyV: "و",
    KeyB: "ب",
    KeyM: "م"
  }
  return singles[code] || null
}

// Shared locale-aware mapper for search bar and Word Decoder physical keyboard input.
export function mapLexiqamusKeyboardEventToOttomanChar({ key, code, shiftKey, altKey, isTurkishLocale }) {
  return isTurkishLocale
    ? ottomanCharForTurkishKey(key, shiftKey, altKey)
    : ottomanCharForPhysicalKey(code, shiftKey, altKey)
}
